/**
 * Visual Policy Builder — Generates visual policy from seed data.
 *
 * ARCHITECTURE v4.0 — Single Source of Truth
 *
 * REFACTORED: Now derives visual policy from actual seed data via
 * generateGovernanceMapFromSeeds() instead of a handmatig maintained JSON file.
 *
 * This eliminates the de-sync problem where VISUAL_GOVERNANCE_MAP.json
 * would fall out of date whenever seeds were added or modified.
 *
 * The builder runs once during policy initialization.
 *
 * @see docs/architecture/VISUAL_AID_SYSTEM.md
 * @see generateGovernanceMap.ts — seed analysis logic
 */

import type { VisualFamilyRule } from './policyTypes'
import {
  generateGovernanceMapFromSeeds,
  type GeneratedVisualGovernanceMap,
  type GeneratedFamilyEntry,
} from './generateGovernanceMap'
import { TAXONOMY } from '../../content'
import type { VisualAidType } from '../../visuals'

// ─── Cached generated map (computed once at init) ─────────────────────────────

let _cachedMap: GeneratedVisualGovernanceMap | null = null

function getMap(): GeneratedVisualGovernanceMap {
  if (!_cachedMap) {
    _cachedMap = generateGovernanceMapFromSeeds()
  }
  return _cachedMap
}

// ─── Builder ──────────────────────────────────────────────────────────────────

export interface BuiltVisualPolicy {
  requiredFamilies: string[]
  familyRules: Record<string, VisualFamilyRule>
}

/**
 * Build the visual policy section from seed-derived governance map.
 *
 * This is the SINGLE SOURCE OF TRUTH for visual governance.
 * - requiredFamilies = all families where stimulus.required === true
 * - familyRules = derived from seed analysis of stimulus/support entries
 *
 * Only families that have non-trivial rules are included in familyRules
 * (i.e., at least one of: stimulus.required, stimulus.default, support.required, support.default).
 */
export function buildVisualPolicyFromMap(): BuiltVisualPolicy {
  const map = getMap()
  const families = map.families

  const requiredFamilies: string[] = []
  const familyRules: Record<string, VisualFamilyRule> = {}

  for (const [familyId, entry] of Object.entries(families)) {
    // Build requiredFamilies: families where stimulus is required
    if (entry.stimulus.required) {
      requiredFamilies.push(familyId)
    }

    // Build familyRule from generated data
    const rule: VisualFamilyRule = {
      stimulusRequired: entry.stimulus.required,
    }

    // Add stimulus default if present
    if (entry.stimulus.default) {
      rule.stimulusDefault = entry.stimulus.default as VisualAidType
    }

    // Add stimulus allowed types (default + alternates)
    if (
      entry.stimulus.default ||
      (entry.stimulus.alternates && entry.stimulus.alternates.length > 0)
    ) {
      const allowed: VisualAidType[] = []
      if (entry.stimulus.default)
        allowed.push(entry.stimulus.default as VisualAidType)
      if (entry.stimulus.alternates) {
        for (const alt of entry.stimulus.alternates) {
          allowed.push(alt as VisualAidType)
        }
      }
      if (allowed.length > 0) rule.stimulusAllowed = allowed
    }

    // Add support fields
    rule.supportRequired = entry.support.required

    if (entry.support.default) {
      rule.supportDefault = entry.support.default as VisualAidType
    }

    // Add support allowed types (default + alternates)
    if (
      entry.support.default ||
      (entry.support.alternates && entry.support.alternates.length > 0)
    ) {
      const allowed: VisualAidType[] = []
      if (entry.support.default)
        allowed.push(entry.support.default as VisualAidType)
      if (entry.support.alternates) {
        for (const alt of entry.support.alternates) {
          allowed.push(alt as VisualAidType)
        }
      }
      if (allowed.length > 0) rule.supportAllowed = allowed
    }

    if (entry.support.showWhen) {
      rule.supportShowWhen = entry.support.showWhen as 'always' | 'wrong'
    }

    familyRules[familyId] = rule
  }

  return { requiredFamilies, familyRules }
}

/**
 * Get the number of families in the governance map.
 * Useful for diagnostics and validation.
 */
export function getMapFamilyCount(): number {
  return Object.keys(getMap().families).length
}

/**
 * Validate that all families in the MAP exist in the taxonomy.
 * Returns array of family IDs that are in the MAP but not in the provided set.
 */
export function findOrphanedMapFamilies(knownFamilyIds: Set<string>): string[] {
  return Object.keys(getMap().families).filter((id) => !knownFamilyIds.has(id))
}

/**
 * Force regeneration of the cached map.
 * Useful after seed data changes (e.g., in tests or hot-reload scenarios).
 */
export function invalidateMapCache(): void {
  _cachedMap = null
}
